import moment from "moment";
import { STATE_RANK } from "@/components/report/shared/group/hasGroupUserRows";

/**
 * "Pregled korisnika" grupne firme (telefon i računar): redovi radnika sa poručeno / preuzeto / razlikom i
 * statusom, čipovi statusa sa brojevima i zbirovi. Zahteva nasleđen stari tab CompanyReportTabEmployee (users,
 * orderStatus, statusItems, userOrders, showCompanyColumn, company). "Preuzeto" postoji samo kad firma prati
 * preuzimanje (company.checkReport); bez toga je radnik samo poručio ili nije.
 */
export default {
  computed: {
    canCheckTaken() {
      return Boolean(this.company && this.company.checkReport);
    },
    isSingleDay() {
      const { from, to } = this.$store.state.report.filters.dates;
      return from === to;
    },
    /** Čipovi statusa sa brojevima; vrednosti su isti statusi kao u starom izborniku (orderStatus). */
    statusChips() {
      const users = this.users || [];
      const ordered = users.filter((user) => (user.package_count || 0) > 0);
      const taken = ordered.filter((user) => (user.package_count_picked || 0) > 0).length;
      const counts = { 0: users.length, 1: ordered.length, 2: users.length - ordered.length, 3: ordered.length - taken, 4: taken };
      return this.statusItems.map((item) => ({ value: item.value, label: item.text, count: counts[item.value] }));
    },
    allRows() {
      return this.userOrders.map((user, index) => this.toRow(user, index));
    },
    /** Radnici (redovi) posle pretrage, po imenu. */
    matchedRows() {
      const search = this.search;
      const matched = search
        ? this.customSearch(this.allRows, [{ value: "name" }, { value: "idText" }, { value: "externalCode" }, { value: "company" }], search)
        : this.allRows;
      return [...matched].sort((a, b) => a.name.localeCompare(b.name));
    },
    sums() {
      return this.matchedRows.reduce(
        (sums, row) => ({
          ordered: sums.ordered + row.ordered,
          taken: sums.taken + row.taken,
          difference: sums.difference + row.difference,
        }),
        { ordered: 0, taken: 0, difference: 0 }
      );
    },
    emptyText() {
      if (this.search) {
        return this.$t("report_mobile.no_results_for", { search: this.search });
      }
      return (this.users || []).length ? this.$t("report_mobile.no_workers_in_view") : this.$t("no_company_report_data");
    },
  },
  methods: {
    /** Red radnika: radnik iz starog taba (userOrders) + stanje narudžbe i preuzimanja. */
    toRow(user, index) {
      const ordered = user.order_count || 0;
      const taken = this.canCheckTaken ? user.order_payed || 0 : 0;
      let state = "off";
      if (ordered > 0) {
        state = !this.canCheckTaken ? "ordered" : taken <= 0 ? "none" : taken >= ordered ? "all" : "part";
      }
      const stamp = user.order_date ? moment(user.order_date, "DD.MM.YYYY HH:mm") : null;
      const name = [user.name, user.lastname].filter(Boolean).join(" ").trim();
      return {
        key: `${user.id}|${user.company_id || user.company_name || ""}|${index}`,
        id: user.id,
        idText: String(user.id),
        externalCode: user.external_code || "",
        name,
        title: user.external_code ? `${name} · ${user.external_code}` : name,
        initials: [user.name, user.lastname]
          .filter(Boolean)
          .map((part) => String(part).charAt(0))
          .join(""),
        company: user.company_name || "",
        time: stamp && stamp.isValid() ? stamp.format("DD.MM. HH:mm") : "",
        sortTime: stamp && stamp.isValid() ? stamp.valueOf() : 0,
        ordered,
        taken,
        difference: this.canCheckTaken ? ordered - taken : 0,
        state,
        stateRank: STATE_RANK[state] ?? 0,
        statusLabel: this.statusLabel(state, taken, ordered),
        source: user,
      };
    },
    statusLabel(state, taken, ordered) {
      if (state === "off") {
        return this.$t("report_desktop.status_not_ordered");
      }
      if (state === "ordered") {
        return this.$t("ordered");
      }
      if (state === "all") {
        return this.$t("report_mobile.status_taken");
      }
      if (state === "part") {
        return this.$t("report_mobile.taken_partly", { taken, total: ordered });
      }
      return this.$t("report_mobile.status_not_taken");
    },
    /** Radnik za mesečni pregled (store: report/selectedEmployee); mesec iz istog izvora kao stari dijalog. */
    toEmployeeContext(row) {
      const companies = this.$store.state.report.selectedFoodCompanies || [];
      const companyName = row.company || (companies.length === 1 ? companies[0].name : "");
      return {
        id: row.id,
        name: this.toLatin(row.source.name),
        lastname: this.toLatin(row.source.lastname),
        fullName: row.name,
        companyId: row.source.company_id || (companies.length === 1 ? companies[0].id : undefined),
        companyName: this.toLatin(companyName || ""),
        monthUrl: "/user/food-orders-by-month",
      };
    },
  },
};
